import { FC, useState, useEffect } from "react"
import styled from "styled-components"
import type { Document } from "@contentful/rich-text-types"
import { Color } from "@lib/types"
import { useFilter, useSort, useTracking } from "@hooks/index"

import { CollectionFilters, ProductGrid } from "@components/collection"
import { Container, CollectionSlider } from "@components/ui"
import { PageSection } from "@components/page"
import { ContentfulImage, ContentfulProductWithShopifyData } from "@lib/types"

interface Props {
  page: {
    name: string
    title: string
    description: {
      json: Document
    }
    sys: {
      id: string
    }
    hideFilter: boolean
    collectionsCollection: {
      items: [
        {
          name: string
          isMainCategory?: boolean
          slug: string
          excerpt: string
          featuredImage: ContentfulImage
        }
      ]
    }
    filters: {
      color: Color[]
      fabricTexture: string[]
      fabricThickness: string[]
      size: string[]
    }
    pageSection: {
      __typename: string
      layout: string
    }
    products: ContentfulProductWithShopifyData[]
  }
}

export const ShopAllPage: FC<Props> = ({ page }) => {
  const {
    title,
    description,
    collectionsCollection: { items: collections },
    pageSection,
    hideFilter,
    products,
    sys
  } = page

  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [collectionProducts, setCollectionProducts] = useState<
    ContentfulProductWithShopifyData[] | []
  >([])

  useEffect(() => {
    const currentCollection = collections[currentSlide]?.name || ""

    const cProducts = collections[currentSlide].isMainCategory ?
    products 
    : products.filter(
      ({ category }) => category?.name === currentCollection
    )

    setCollectionProducts(cProducts)

    const p = cProducts.map((p, i) => ({
      name: `${p.title}${p.color ? ` - ${p?.color?.name}` : ''}`,
      position: i,
      category: p?.category?.name,
      url: `https://www.hautehijab.com/products/${p.slug}`,
    }))

    const contentsForTikTok = cProducts.map( p => ({
      content_id: p.slug,
      content_type: 'product',
      content_name: `${p.title}${p.color ? ` - ${p?.color?.name}` : ''}`,
      quantity: 1,
    }))
    track("Product List Viewed", {
      list_id: `${title} - ${currentCollection}`,
      category: currentCollection,
      products: p,
    })
    tikTokTrack("ViewContent", {
      content_category: "Shop All Page",
      content_name: `${title} - ${currentCollection}`,
      contents: contentsForTikTok
    })
  }, [currentSlide])

  const {
    state,
    filteredProducts,
    handleChange: handleFilterChange,
    clearFilters,
  } = useFilter(collectionProducts)
  const { isActive: filtersAreActive } = state

  const {
    sortIsActive,
    sortKey,
    handleChange: handleSortChange,
    sortOptions,
    sortBy,
  } = useSort()

  const isActive = filtersAreActive || sortIsActive

  const { track, tikTokTrack } = useTracking()

  const sortedProducts = sortBy(filteredProducts)
  const productCount = sortedProducts.length

  useEffect(() => {
    if (isActive) {
      const currentCollection = collections[currentSlide]?.name || ""

      const filters = Object.entries(state.filters).flatMap((entry: any) =>
        entry[1].flatMap((value: string) => ({
          type: entry[0],
          value,
        }))
      )

      const products = sortedProducts.map((p, i) => ({
        name: `${p.title} - ${p?.color?.name}`,
        position: i,
        category: p?.category?.name,
        url: `https://www.hautehijab.com/products/${p.slug}`,
      }))

      track("Product List Filtered", {
        list_id: `${title} - ${currentCollection}`,
        category: currentCollection,
        filters,
        sorts: [
          {
            type: "sort",
            value: sortKey,
          },
        ],
        products,
      })
    }
  }, [state, sortKey])

  return (
    <StyledShopAllPage>
      <Header>
        <SliderWrapper>
          <CollectionSlider
            key={sys.id}
            title={title}
            description={description}
            slides={collections}
            currentSlide={currentSlide}
            setCurrentSlide={setCurrentSlide}
          />
        </SliderWrapper>
        <StyledContainer>
          {!hideFilter && 
            <CollectionFilters
              filters={page.filters}
              filterState={state.filters}
              handleFilterChange={handleFilterChange}
              clearFilters={clearFilters}
              sortValue={sortKey}
              sortOptions={sortOptions}
              handleSortChange={handleSortChange}
              productCount={productCount}
            />
          }
        </StyledContainer>
      </Header>
      <StyledContainer>
        {productCount > 0 && (
          <Collection>
            <ProductGrid products={sortedProducts} />
          </Collection>
        )}
      </StyledContainer>
      {pageSection && <PageSection data={pageSection} />}
    </StyledShopAllPage>
  )
}

const StyledShopAllPage = styled.div``

const StyledContainer = styled(Container)`
  margin: 0 0 5rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 auto 11rem;
    max-width: 110rem;
  }
`

const Header = styled.div`
  padding: 3rem 0 12.1rem;
  background: ${({ theme }) => theme.color.beige};

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    padding: 2.7rem 0 19.6rem;
  }
`

const SliderWrapper = styled.div`
  margin: 0 0 2.8rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 0 5.4rem;
  }
`

const Collection = styled.div`
  margin-top: -12rem;
  padding: 0 1.6rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin-top: -19.5rem;
    padding: 0;
  }
`
